/*
  Clone of InvoiceDeliveryTable for the delivery-and-payment view (EMS-10148).
  Differs from the original in one way today: the Uploaded column is merged
  into File, with the upload timestamp rendered beneath the file name and the
  date sorter moved onto File. The Stripe payment columns get added here.
*/
import React from 'react'
import isNil from 'lodash/isNil'
import styles from './InvoiceDeliveryTable.module.scss'
import { Popconfirm, Tooltip } from 'antd'
import { Select, Table, Input } from '@surecompanies/core_components'
import {
  DeleteOutlined,
  DollarOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  LoadingOutlined,
  SearchOutlined,
  SendOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table/interface'
import {
  InvoiceDeliveryItemDto,
  InvoiceDeliveryStatus,
  InvoicePaymentType,
  InvoiceTemplate,
} from '../../api/dto/invoice-delivery.dto'
import { TemplateFilter } from './emailTemplates/TemplateFilter'
import { SelectedTemplateSummary } from './emailTemplates/SelectedTemplateSummary'
import templateRowStyles from './emailTemplates/templateRows.module.scss'
import { EmailTemplateTag } from './emailTemplates/EmailTemplateTag'
import { useDeliveryRowSelection } from './useDeliveryRowSelection'
import {
  buildUploadTimeSorter,
  type TemplateRowInfo,
} from './emailTemplates/useTemplateRows'
import {
  ArchiveButton,
  CompanyCell,
  FileNameCell,
  IconButton,
  IssuesTag,
  SendButton,
  StatusBadge,
  UnmatchedTag,
  VersionBadge,
} from './BulkInvoiceUpload.styles'
import {
  FilterTab,
  FilterTabs,
  FloatingBar,
  FloatingBarButton,
  FloatingBarCount,
  FloatingBarDivider,
  ResultsCount,
  SearchDivider,
  SearchRow,
  StatusCell,
  DeliveryTableCard,
} from './InvoiceDeliveryTable.styles'
import {
  ActionButtonsRowRight,
  AmountCell,
  DrawButton,
  DrawStatusBadge,
  PaymentMethodPill,
} from './InvoiceDeliveryTableWithStripePayment.styles'
import { formatCurrency } from './generation/utils'
import { DeliveryRatioBadge } from './DeliveryRatioBadge'
import {
  IN_PROGRESS_STATUSES,
  NOT_STARTED_STATUS,
  canDrawDelivery,
  centsToDollars,
  dollarsToCents,
  normalizePaymentType,
  resolveDrawDisabledReason,
  resolveDrawStatus,
  shouldShowDrawAction,
} from './paymentStatus'
import { ExpandedHistoryRowsWithStripePayment } from './ExpandedHistoryRowsWithStripePayment'
import { UploadedMeta } from './UploadedMeta'
import { TruncatedFileName } from './TruncatedFileName'
import { useTableColumnWidths } from './useTableColumnWidths'
import {
  DELIVERY_WITH_PAYMENT_COLUMN_WIDTH_BY_TITLE,
  DELIVERY_WITH_PAYMENT_COLUMN_WIDTHS,
  EXPAND_COLUMN_WIDTH,
} from './deliveryTableColumns'
import RequirePermission from 'enrollment-platform/rbac/RequirePermission'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'
import {
  canSendDelivery,
  cleanParseWarningRawValue,
  formatDueDate,
  hasBeenSent,
  resolveSendDisabledReason,
  shouldShowSendAction,
  resolveStatusBadgeKey,
  resolveStatusLabel,
  triggerFileDownload,
  DELIVERY_DEFAULT_PAGE_SIZE,
  DELIVERY_PAGE_SIZE_OPTIONS,
} from './utils'

/*
 * AntD v5.21's Pagination wraps the size-changer Select with `showSearch: true`
 * hardcoded (see antd/lib/pagination/Select.js → MiniSelect/MiddleSelect),
 * which renders the "25 / page" control as a typeable combobox that shows
 * "No data" on garbage input. Pass this non-searchable Select via the
 * Pagination's `selectComponentClass` prop to restore plain dropdown UX.
 *
 * `Option` static must be attached because rc-pagination's Options renders
 * children as `<Select.Option>` (rc-pagination/lib/Options.js).
 */
const NonSearchableSelect = Object.assign(
  (props: React.ComponentProps<typeof Select>) => (
    <Select {...props} showSearch={false} size="small" />
  ),
  { Option: Select.Option }
)

/*
 * A money cell renders only for a real number. `null`/`undefined` mean the
 * value is genuinely absent (unparsed Total Due, or a draw that has not
 * settled) and must read as a dash rather than $0.00 — a zero here would be a
 * factual claim about an amount nobody has established.
 */
const isFiniteAmount = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const STATUS_FILTER_OPTIONS: Array<{
  label: string
  value: InvoiceDeliveryStatus | undefined
}> = [
  { label: 'All', value: undefined },
  { label: 'Draft', value: InvoiceDeliveryStatus.Draft },
  { label: 'Sending', value: InvoiceDeliveryStatus.Sending },
  { label: 'Delivered', value: InvoiceDeliveryStatus.Delivered },
  { label: 'Failed', value: InvoiceDeliveryStatus.Failed },
]

const FALLBACK_COLUMN_WIDTHS = DELIVERY_WITH_PAYMENT_COLUMN_WIDTHS
const W = DELIVERY_WITH_PAYMENT_COLUMN_WIDTH_BY_TITLE

function sumWidths(widths: readonly number[]): number {
  return widths.reduce((acc, w) => acc + w, 0)
}

interface InvoiceDeliveryTableWithStripePaymentProps {
  deliveries: InvoiceDeliveryItemDto[]
  /** The whole month, since a selection can include rows the filters now hide. */
  monthDeliveries: InvoiceDeliveryItemDto[]
  total: number
  isLoading: boolean
  search: string
  onSearchChange: (value: string) => void
  statusFilter: InvoiceDeliveryStatus | undefined
  onStatusFilterChange: (status: InvoiceDeliveryStatus | undefined) => void
  templateFilter: InvoiceTemplate | undefined
  /** Templates the billing month allows, for the filter. */
  templateOptions: readonly InvoiceTemplate[]
  onTemplateFilterChange: (template: InvoiceTemplate | undefined) => void
  showArchived: boolean
  onToggleArchived: () => void
  onSend: (id: string) => void
  /**
   * Starts a Stripe draw for one delivery. Optional while the draw endpoint is
   * still being built (EMS-10148): without a handler the Draw button renders
   * disabled and says why, rather than silently doing nothing when clicked.
   */
  onDraw?: (id: string) => void
  onBulkSend: () => void
  onDelete: (id: string) => void
  onSetArchived: (id: string, isArchived: boolean) => void
  onRowClick: (delivery: InvoiceDeliveryItemDto) => void
  selectedRowKeys: React.Key[]
  /** How many of the selected rows are actually sendable. */
  selectedSendableCount: number
  onSelectedRowKeysChange: (keys: React.Key[]) => void
  isMutating: boolean
  sendingIds: Set<string>
  isBulkSending: boolean
  /**
   * False, for users who may not download invoices, hides "Download Selected"
   * and shows file names as plain text instead of download links.
   */
  canDownloadInvoices: boolean
  onDownloadSelected: () => void
  isDownloadingArchive: boolean
  /** Bytes received so far, pre-formatted. Null when no download is running. */
  downloadedBytesLabel: string | null
  getTemplateRowInfo: (id: string) => TemplateRowInfo
}

const InvoiceDeliveryTableWithStripePayment: React.FC<
  InvoiceDeliveryTableWithStripePaymentProps
> = ({
  deliveries,
  monthDeliveries,
  total,
  isLoading,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  templateFilter,
  templateOptions,
  onTemplateFilterChange,
  showArchived,
  onToggleArchived,
  onSend,
  onDraw,
  onBulkSend,
  onDelete,
  onSetArchived,
  onRowClick,
  selectedRowKeys,
  selectedSendableCount,
  onSelectedRowKeysChange,
  isMutating,
  sendingIds,
  isBulkSending,
  canDownloadInvoices,
  onDownloadSelected,
  isDownloadingArchive,
  downloadedBytesLabel,
  getTemplateRowInfo,
}) => {
  const { containerRef: tableCardRef, columnWidths: measuredColumnWidths } =
    useTableColumnWidths(FALLBACK_COLUMN_WIDTHS)

  const columns: ColumnsType<InvoiceDeliveryItemDto> = [
    {
      title: 'Company',
      dataIndex: 'clientName',
      width: W.company,
      ellipsis: true,
      align: 'left',
      sorter: (a, b) => (a.clientName ?? '').localeCompare(b.clientName ?? ''),
      render: (_: string | null, record: InvoiceDeliveryItemDto) => (
        <CompanyCell>
          <div className={styles.companyCellInner}>
            <span>
              {record.clientName ?? <em className={styles.emptyDash}>—</em>}
            </span>
            {record.version > 1 && (
              <Tooltip title="Click the row to view previous versions">
                <VersionBadge>v{record.version}</VersionBadge>
              </Tooltip>
            )}
          </div>
          {!record.clientId && (
            <UnmatchedTag>
              <WarningOutlined />
              Unmatched Company
            </UnmatchedTag>
          )}
          {record.parseWarnings
            // A file can contain more than one "Total Due" cell (e.g. a section
            // total and a grand total); surface a single tag, not one per cell.
            ?.slice(0, 1)
            .map((w) => {
              const prefix = w.kind === 'missing' ? 'Missing' : 'Invalid'
              const rawValue = cleanParseWarningRawValue(w.rawValue)
              const tooltip = w.cellAddress
                ? `Cell ${w.cellAddress}: ${rawValue ? `"${rawValue}", ` : ''}${w.reason}. Click the row to fix.`
                : `${w.reason}. Click the row to fix.`
              return (
                <Tooltip key={`${w.field}-${w.cellAddress}`} title={tooltip}>
                  <IssuesTag>
                    <WarningOutlined />
                    {prefix} {w.label}
                  </IssuesTag>
                </Tooltip>
              )
            })}
        </CompanyCell>
      ),
    },
    {
      title: 'Email template',
      key: 'invoiceTemplate',
      width: W.template,
      align: 'left',
      render: (_: unknown, record: InvoiceDeliveryItemDto) => (
        <EmailTemplateTag template={record.invoiceTemplate} />
      ),
    },
    {
      title: 'File',
      dataIndex: 'fileName',
      width: W.file,
      align: 'left',
      /*
       * The upload timestamp lives under the file name rather than in its own
       * column, so this column inherits the date sorter (and the table's
       * default newest-first order) that Uploaded used to own.
       */
      sorter: buildUploadTimeSorter(getTemplateRowInfo),
      defaultSortOrder: 'descend',
      render: (name: string, record: InvoiceDeliveryItemDto) => (
        <div className={styles.fileCellStack}>
          {record.downloadUrl && canDownloadInvoices ? (
            <FileNameCell
              $clickable
              onClick={(e) =>
                triggerFileDownload(
                  e,
                  record.downloadUrl ?? '',
                  record.fileName
                )
              }
            >
              <FileExcelOutlined className={styles.fileIcon} />
              <TruncatedFileName name={name} companyName={record.clientName} />
            </FileNameCell>
          ) : (
            <FileNameCell>
              <FileExcelOutlined className={styles.fileIcon} />
              <TruncatedFileName name={name} companyName={record.clientName} />
            </FileNameCell>
          )}
          <UploadedMeta createdAt={record.createdAt} />
        </div>
      ),
    },
    {
      title: 'Email status',
      dataIndex: 'status',
      width: W.status,
      render: (
        _status: InvoiceDeliveryStatus,
        record: InvoiceDeliveryItemDto
      ) => (
        <StatusCell>
          <StatusBadge $status={resolveStatusBadgeKey(record)}>
            {resolveStatusLabel(record)}
          </StatusBadge>
          <DeliveryRatioBadge stats={record.emailStats} />
        </StatusCell>
      ),
    },
    {
      title: 'Payment method',
      dataIndex: 'paymentType',
      width: W.paymentMethod,
      sorter: (a, b) =>
        (a.paymentType ?? '').localeCompare(b.paymentType ?? ''),
      render: (_, record: InvoiceDeliveryItemDto) => {
        const paymentType = normalizePaymentType(record.paymentType)
        if (paymentType === null) {
          return (
            <Tooltip
              title={
                record.clientId
                  ? 'No billing profile for this client, so no payment type is set.'
                  : 'Unmatched company — assign a client to see its payment type.'
              }
            >
              <em className={styles.emptyDash}>—</em>
            </Tooltip>
          )
        }
        return (
          <PaymentMethodPill $type={paymentType}>
            {paymentType === InvoicePaymentType.WIRE ? 'Wire' : 'Stripe'}
          </PaymentMethodPill>
        )
      },
    },
    {
      title: 'Amount',
      key: 'amount',
      width: W.amount,
      align: 'right',
      /*
       * The invoiced amount, parsed out of the uploaded file. A row whose
       * Total Due cell was unreadable has no value here — that is what the
       * parse-warning tag on the Company column is telling the operator to fix.
       */
      sorter: (a, b) =>
        (a.invoiceMetadata?.totalDue ?? 0) - (b.invoiceMetadata?.totalDue ?? 0),
      render: (_, record: InvoiceDeliveryItemDto) => {
        const amount = record.invoiceMetadata?.totalDue
        return isFiniteAmount(amount) ? (
          <AmountCell>{formatCurrency(amount)}</AmountCell>
        ) : (
          <em className={styles.emptyDash}>—</em>
        )
      },
    },
    {
      title: 'Due date',
      dataIndex: 'dueDate',
      width: W.dueDate,
      align: 'left',
      render: (_: string | null, record: InvoiceDeliveryItemDto) => {
        const due = formatDueDate(record.dueDate)
        return due === null ? (
          <em className={styles.emptyDash}>—</em>
        ) : (
          <span>{due}</span>
        )
      },
    },
    {
      title: 'Actual amount',
      key: 'actualAmount',
      width: W.actualAmount,
      align: 'right',
      /*
       * The total that actually settled, which can differ from the invoiced
       * amount after a partial or adjusted draw. Blank until something settles.
       */
      sorter: (a, b) =>
        (a.paymentDetail?.totalSucceededAmount ?? 0) -
        (b.paymentDetail?.totalSucceededAmount ?? 0),
      render: (_, record: InvoiceDeliveryItemDto) => {
        const actualAmountCents = record.paymentDetail?.totalSucceededAmount
        if (!isFiniteAmount(actualAmountCents))
          return <em className={styles.emptyDash}>—</em>

        const invoicedAmount = record.invoiceMetadata?.totalDue
        const overallStatus = record.paymentDetail?.overallStatus
        // Compare in cents, not dollars, so float rounding never produces a
        // false mismatch between two amounts that are actually equal. A draw
        // that hasn't started, or is still in flight (e.g. processing), has
        // nothing final to compare against — flagging it would just warn
        // about every untouched or not-yet-settled invoice.
        const isMismatched =
          overallStatus !== NOT_STARTED_STATUS &&
          !(overallStatus && IN_PROGRESS_STATUSES.has(overallStatus)) &&
          isFiniteAmount(invoicedAmount) &&
          dollarsToCents(invoicedAmount) !== actualAmountCents

        return (
          <AmountCell $mismatched={isMismatched}>
            {formatCurrency(centsToDollars(actualAmountCents))}
            {isMismatched && (
              <Tooltip
                title={`Does not match the invoiced amount of ${formatCurrency(invoicedAmount)}`}
              >
                <WarningOutlined style={{ marginLeft: 6, color: '#f59e0b' }} />
              </Tooltip>
            )}
          </AmountCell>
        )
      },
    },
    {
      title: 'Drawn status',
      key: 'drawStatus',
      width: W.drawStatus,
      sorter: (a, b) =>
        (resolveDrawStatus(a)?.label ?? '').localeCompare(
          resolveDrawStatus(b)?.label ?? ''
        ),
      render: (_, record: InvoiceDeliveryItemDto) => {
        const drawStatus = resolveDrawStatus(record)
        if (!drawStatus) return <em className={styles.emptyDash}>—</em>
        const { label, tone, succeededCount, totalCount } = drawStatus
        return (
          <DrawStatusBadge $tone={tone}>
            {totalCount > 0
              ? `${label} (${succeededCount}/${totalCount})`
              : label}
          </DrawStatusBadge>
        )
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: W.actions,
      align: 'right',
      render: (_, record: InvoiceDeliveryItemDto) => {
        const sendDisabledReason = resolveSendDisabledReason(record)
        const canSend = canSendDelivery(record) && isNil(sendDisabledReason)

        const isSending = sendingIds.has(record.id)
        /*
         * A row that has already been dispatched reads "Resend", because
         * sending it again may contact recipients who already hold the
         * invoice. The label is driven by the record's own history, so a
         * blocked row still says "Resend" — reading "Send" on an invoice that
         * has already gone out misrepresents what the button would do.
         */
        const isResend = hasBeenSent(record)
        const sendLabel = isResend ? 'Resend' : 'Send'

        const paymentType = normalizePaymentType(record.paymentType)
        const canDraw = canDrawDelivery(record, paymentType)
        const drawDisabledReason = onDraw
          ? resolveDrawDisabledReason(record, paymentType)
          : 'Drawing payment is not available yet.'

        return (
          <ActionButtonsRowRight>
            <RequirePermission
              action={RbacActions.Draw}
              subject={RbacSubjects.FundingDraw}
            >
              {/*
                Wire, archived, and historical rows are never drawn against, so
                the button is absent rather than disabled. Every other block (no
                profile yet, no Stripe account, draw already in flight) is
                recoverable and renders disabled with its reason.
              */}
              {!shouldShowDrawAction(record) ? null : canDraw && onDraw ? (
                <DrawButton
                  onClick={(e) => {
                    e.stopPropagation()
                    onDraw(record.id)
                  }}
                  disabled={isMutating}
                >
                  <DollarOutlined /> Draw
                </DrawButton>
              ) : (
                <Tooltip title={drawDisabledReason}>
                  <span
                    className={styles.disabledWrapper}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DrawButton disabled className={styles.noPointerEvents}>
                      <DollarOutlined /> Draw
                    </DrawButton>
                  </span>
                </Tooltip>
              )}
            </RequirePermission>

            {!shouldShowSendAction(record) ? null : canSend ? (
              /*
               * Both cases call onSend; the hook decides. A resend opens the
               * review modal so the operator picks recipients, while a draft
               * dispatches straight away — no interstitial is warranted when
               * nobody can receive a duplicate.
               */
              <SendButton
                onClick={(e) => {
                  e.stopPropagation()
                  onSend(record.id)
                }}
                disabled={isSending}
              >
                {isSending ? <LoadingOutlined /> : <SendOutlined />} {sendLabel}
              </SendButton>
            ) : (
              /*
               * Recoverable blocks render disabled with the reason rather than
               * vanishing — an empty cell gives the operator no way to find out
               * what to fix. Rows that can never be sent (archived, historical)
               * are filtered out above and show nothing at all.
               */
              <Tooltip title={sendDisabledReason ?? ''}>
                <span
                  className={styles.disabledWrapper}
                  onClick={(e) => e.stopPropagation()}
                >
                  <SendButton disabled className={styles.noPointerEvents}>
                    <SendOutlined /> {sendLabel}
                  </SendButton>
                </span>
              </Tooltip>
            )}

            <ArchiveButton
              $archived={record.isArchived}
              onClick={(e) => {
                e.stopPropagation()
                onSetArchived(record.id, !record.isArchived)
              }}
              disabled={isMutating}
            >
              {record.isArchived ? 'Unarchive' : 'Archive'}
            </ArchiveButton>

            <Popconfirm
              title="Delete delivery"
              description={
                record.version > 1
                  ? 'This deletes the latest version. The previous version will become the new latest.'
                  : 'This will permanently remove the record. Are you sure?'
              }
              onConfirm={() => onDelete(record.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
            >
              <IconButton $color="#dc2626" disabled={isMutating}>
                <DeleteOutlined />
              </IconButton>
            </Popconfirm>
          </ActionButtonsRowRight>
        )
      },
    },
  ]

  const rowSelection = useDeliveryRowSelection(
    deliveries,
    selectedRowKeys,
    onSelectedRowKeysChange
  )

  return (
    <>
      <SearchRow>
        <Input
          prefix={<SearchOutlined className={styles.searchIcon} />}
          placeholder="Search company or file name..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className={styles.searchInput}
        />

        <TemplateFilter
          value={templateFilter}
          templates={templateOptions}
          onChange={onTemplateFilterChange}
        />

        <FilterTabs>
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <FilterTab
              key={opt.label}
              $active={statusFilter === opt.value}
              onClick={() => onStatusFilterChange(opt.value)}
            >
              {opt.label}
            </FilterTab>
          ))}
          <SearchDivider />
          <FilterTab $active={showArchived} onClick={onToggleArchived}>
            {showArchived ? 'Hide archived' : 'Show archived'}
          </FilterTab>
        </FilterTabs>

        <ResultsCount>
          Showing <strong>{deliveries.length}</strong> of {total} deliveries
        </ResultsCount>
      </SearchRow>

      <DeliveryTableCard>
        <div ref={tableCardRef}>
          <Table
            dataSource={deliveries}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            rowSelection={rowSelection}
            style={{ minWidth: sumWidths(FALLBACK_COLUMN_WIDTHS) }}
            expandable={{
              columnWidth: EXPAND_COLUMN_WIDTH,
              rowExpandable: (record) => record.version > 1,
              expandedRowRender: (record) => (
                <ExpandedHistoryRowsWithStripePayment
                  deliveryId={record.id}
                  onSetArchived={onSetArchived}
                  onDelete={onDelete}
                  isMutating={isMutating}
                  canDownload={canDownloadInvoices}
                  onDownload={triggerFileDownload}
                  columnWidths={measuredColumnWidths}
                  showArchived={showArchived}
                />
              ),
              expandRowByClick: false,
            }}
            onRow={(record) => ({
              onClick: (e) => {
                const target = e.target as HTMLElement
                if (
                  target.closest('button') ||
                  target.closest('a') ||
                  target.closest('.ant-table-row-expand-icon') ||
                  target.closest('.ant-checkbox-wrapper') ||
                  target.closest('.ant-table-selection-column')
                ) {
                  return
                }
                onRowClick(record)
              },
              className: `${styles.tableRow} ${record.isArchived ? styles.tableRowArchived : ''} ${getTemplateRowInfo(record.id).isPairSecond ? templateRowStyles.pairSecondRow : ''}`,
            })}
            pagination={{
              defaultPageSize: DELIVERY_DEFAULT_PAGE_SIZE,
              pageSizeOptions: DELIVERY_PAGE_SIZE_OPTIONS,
              showSizeChanger: true,
              selectComponentClass: NonSearchableSelect,
              showTotal: (total, range) =>
                `${range[0]}–${range[1]} of ${total}`,
            }}
            size="small"
          />
        </div>
      </DeliveryTableCard>

      {selectedRowKeys.length > 0 && (
        <FloatingBar>
          <FloatingBarCount>{selectedRowKeys.length} selected</FloatingBarCount>
          <SelectedTemplateSummary
            deliveries={monthDeliveries}
            selectedRowKeys={selectedRowKeys}
          />
          <FloatingBarDivider />
          {canDownloadInvoices && (
            <>
              <FloatingBarButton
                onClick={onDownloadSelected}
                disabled={isDownloadingArchive}
              >
                {isDownloadingArchive ? (
                  <LoadingOutlined />
                ) : (
                  <DownloadOutlined />
                )}{' '}
                {isDownloadingArchive
                  ? `Downloading… ${downloadedBytesLabel ?? ''}`
                  : 'Download Selected'}
              </FloatingBarButton>
              <FloatingBarDivider />
            </>
          )}
          {/*
            Opens the review modal rather than dispatching. A mixed selection
            is exactly where sending blind is most dangerous: it can contain
            invoices that have already reached their recipients.

            Counts the sendable subset, not the raw selection — the checkboxes
            also cover download-only rows, so "12 selected" can legitimately
            mean only 4 are sendable. Showing the raw count here would promise
            sends that never happen.
          */}
          {/*
            A disabled button receives no pointer events, so a Tooltip wrapping
            it directly would never fire. Hover has to land on an enabled
            element: the span takes the pointer events while `noPointerEvents`
            lets them pass through the button — the same pairing used by the
            per-row Send control above.
          */}
          <Tooltip
            title={
              selectedSendableCount === 0
                ? 'None of the selected invoices can be sent'
                : ''
            }
          >
            <span className={styles.disabledWrapper}>
              <FloatingBarButton
                $variant="approve"
                onClick={onBulkSend}
                disabled={isMutating || selectedSendableCount === 0}
                className={
                  selectedSendableCount === 0
                    ? styles.noPointerEvents
                    : undefined
                }
              >
                {isBulkSending ? <LoadingOutlined /> : <SendOutlined />} Review
                &amp; send
                {selectedSendableCount !== selectedRowKeys.length &&
                  ` (${selectedSendableCount})`}
              </FloatingBarButton>
            </span>
          </Tooltip>
        </FloatingBar>
      )}
    </>
  )
}

export { InvoiceDeliveryTableWithStripePayment }
